import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { format } from "date-fns";
import { shareToApp, shareViaWebAPI } from "@/hooks/useNativeShare";
import {
  Share2, Copy, Heart, MapPin, Compass, 
  Star, Target, Check, Download, X, Instagram, Music2
} from "lucide-react";
import { FoundingBadge } from "./FoundingBadge";

const CATEGORIES = {
  travel: { icon: MapPin, color: "#E07A5F", label: "Travel" },
  adventures: { icon: Compass, color: "#81B29A", label: "Adventures" },
  experiences: { icon: Star, color: "#F2CC8F", label: "Experiences" },
  goals: { icon: Target, color: "#264653", label: "Goals" },
  romance: { icon: Heart, color: "#EF476F", label: "Romance" },
};

export const ShareAchievementModal = ({ item, partnerName, userName, open, onClose, foundingExplorer, foundingExplorerNumber }) => {
  const [copying, setCopying] = useState(false);
  
  if (!item) return null;

  const category = CATEGORIES[item.category] || CATEGORIES.experiences;
  const CategoryIcon = category.icon;
  
  // Determine the display date - prefer travel date over completion date
  const displayDate = item.target_date || item.end_date || item.completed_at;
  const dateLabel = item.target_date || item.end_date ? "Traveled on" : "Completed on";
  
  const achievementText = partnerName 
    ? `${userName} & ${partnerName} achieved a bucket list goal: "${item.title}" 🎉`
    : `${userName} achieved a bucket list goal: "${item.title}" 🎉`;
  
  const shareUrl = window.location.origin;
  const fullShareText = `${achievementText}\n\nTrack your shared dreams at DuoLyst! ${shareUrl}`;

  const handleCopyLink = async () => {
    setCopying(true);
    try {
      await navigator.clipboard.writeText(fullShareText);
      toast.success("Achievement copied to clipboard!");
    } catch (error) {
      // Fallback for browsers that don't support clipboard API
      const textArea = document.createElement("textarea");
      textArea.value = fullShareText;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand("copy");
      document.body.removeChild(textArea);
      toast.success("Achievement copied to clipboard!");
    }
    setCopying(false);
  };

  const handleShareTwitter = () => {
    shareToApp("twitter", achievementText, shareUrl);
  };

  const handleShareFacebook = () => {
    shareToApp("facebook", achievementText, shareUrl);
  };

  const handleShareInstagram = async () => {
    try {
      await navigator.clipboard.writeText(fullShareText);
      toast.success("Caption copied! Opening Instagram...", {
        description: "Paste the caption in your Instagram post or story"
      });
    } catch (error) {
      handleCopyLink();
    }
    shareToApp("instagram", fullShareText);
  };

  const handleShareTikTok = async () => {
    try {
      await navigator.clipboard.writeText(fullShareText);
      toast.success("Caption copied! Opening TikTok...", {
        description: "Paste the caption in your TikTok video"
      });
    } catch (error) {
      handleCopyLink();
    }
    shareToApp("tiktok", fullShareText);
  };

  const handleNativeShare = async () => {
    const shared = await shareViaWebAPI(achievementText, shareUrl, "DuoLyst Achievement");
    if (!shared) handleCopyLink();
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md bg-card border-none rounded-2xl p-0 overflow-hidden">
        <DialogHeader className="p-6 pb-0">
          <DialogTitle className="font-serif text-2xl text-charcoal flex items-center gap-2">
            <Share2 className="w-6 h-6 text-primary" />
            Share Achievement
          </DialogTitle>
        </DialogHeader>

        <div className="p-6 space-y-6">
          {/* Achievement Card Preview */}
          <div 
            className="relative rounded-2xl overflow-hidden shadow-lg"
            style={{ background: `linear-gradient(135deg, ${category.color}15 0%, ${category.color}05 100%)` }}
            data-testid="achievement-card"
          >
            {/* Decorative elements */}
            <div 
              className="absolute top-0 right-0 w-32 h-32 rounded-full opacity-10"
              style={{ background: category.color, transform: "translate(30%, -30%)" }}
            />
            <div 
              className="absolute bottom-0 left-0 w-24 h-24 rounded-full opacity-10"
              style={{ background: category.color, transform: "translate(-30%, 30%)" }}
            />
            
            <div className="relative p-6">
              {/* Header with check */}
              <div className="flex items-center justify-center mb-4">
                <div 
                  className="w-16 h-16 rounded-full flex items-center justify-center"
                  style={{ background: category.color }}
                >
                  <Check className="w-8 h-8 text-white" />
                </div>
              </div>

              {/* Achievement text */}
              <div className="text-center mb-4">
                <p className="text-xs uppercase tracking-widest font-semibold text-muted-foreground mb-2">
                  Bucket List Achievement
                </p>
                <h3 className="font-serif text-2xl font-medium text-charcoal mb-2">
                  {item.title}
                </h3>
                {item.description && (
                  <p className="text-sm text-muted-foreground line-clamp-2">
                    {item.description}
                  </p>
                )}
              </div>

              {/* Category badge */}
              <div className="flex items-center justify-center gap-2 mb-4">
                <span 
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold"
                  style={{ background: `${category.color}20`, color: category.color }}
                >
                  <CategoryIcon className="w-3 h-3" />
                  {category.label}
                </span>
              </div>

              {/* Names */}
              <div className="text-center">
                <p className="text-sm font-medium text-charcoal flex items-center justify-center gap-2">
                  {partnerName ? (
                    <>
                      <span className="text-primary">{userName}</span>
                      {foundingExplorer && (
                        <FoundingBadge size="xs" founderNumber={foundingExplorerNumber} />
                      )}
                      <span className="text-muted-foreground"> & </span>
                      <span className="text-secondary">{partnerName}</span>
                    </>
                  ) : (
                    <>
                      <span className="text-primary">{userName}</span>
                      {foundingExplorer && (
                        <FoundingBadge size="xs" founderNumber={foundingExplorerNumber} />
                      )}
                    </>
                  )}
                </p>
                {displayDate && (
                  <p className="text-xs text-muted-foreground mt-1">
                    {dateLabel} {format(new Date(displayDate), "MMMM d, yyyy")}
                  </p>
                )}
              </div>

              {/* DuoLyst branding */}
              <div className="flex items-center justify-center gap-1.5 mt-4 pt-4 border-t border-charcoal/10">
                <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                  <Heart className="w-2.5 h-2.5 text-white" />
                </div>
                <span className="text-xs font-medium text-muted-foreground">DuoLyst</span>
              </div>
            </div>
          </div>

          {/* Share buttons */}
          <div className="space-y-3">
            {/* Native share (mobile) */}
            {navigator.share && (
              <Button
                onClick={handleNativeShare}
                className="w-full h-12 bg-primary text-white hover:bg-primary/90 rounded-xl btn-press"
                data-testid="native-share-btn"
              >
                <Share2 className="w-5 h-5 mr-2" />
                Share Achievement
              </Button>
            )}

            {/* Social share buttons */}
            <div className="grid grid-cols-5 gap-2">
              <Button
                variant="outline"
                onClick={handleCopyLink}
                disabled={copying}
                className="h-12 rounded-xl border-charcoal/10 hover:bg-charcoal/5 flex flex-col items-center justify-center gap-1"
                data-testid="copy-achievement-btn"
                title="Copy to clipboard"
              >
                <Copy className="w-5 h-5" />
                <span className="text-[10px]">Copy</span>
              </Button>
              
              <Button
                variant="outline"
                onClick={handleShareTwitter}
                className="h-12 rounded-xl border-charcoal/10 hover:bg-[#1DA1F2]/10 hover:border-[#1DA1F2]/30 hover:text-[#1DA1F2] flex flex-col items-center justify-center gap-1"
                data-testid="twitter-share-btn"
                title="Share on X (Twitter)"
              >
                <Twitter className="w-5 h-5" />
                <span className="text-[10px]">X</span>
              </Button>
              
              <Button
                variant="outline"
                onClick={handleShareFacebook}
                className="h-12 rounded-xl border-charcoal/10 hover:bg-[#4267B2]/10 hover:border-[#4267B2]/30 hover:text-[#4267B2] flex flex-col items-center justify-center gap-1"
                data-testid="facebook-share-btn"
                title="Share on Facebook"
              >
                <Facebook className="w-5 h-5" />
                <span className="text-[10px]">Facebook</span>
              </Button>

              <Button
                variant="outline"
                onClick={handleShareInstagram}
                className="h-12 rounded-xl border-charcoal/10 hover:bg-gradient-to-br hover:from-[#833AB4]/10 hover:via-[#FD1D1D]/10 hover:to-[#F77737]/10 hover:border-[#E1306C]/30 hover:text-[#E1306C] flex flex-col items-center justify-center gap-1"
                data-testid="instagram-share-btn"
                title="Share on Instagram"
              >
                <Instagram className="w-5 h-5" />
                <span className="text-[10px]">Instagram</span>
              </Button>

              <Button
                variant="outline"
                onClick={handleShareTikTok}
                className="h-12 rounded-xl border-charcoal/10 hover:bg-black/5 hover:border-black/30 hover:text-black flex flex-col items-center justify-center gap-1"
                data-testid="tiktok-share-btn"
                title="Share on TikTok"
              >
                <Music2 className="w-5 h-5" />
                <span className="text-[10px]">TikTok</span>
              </Button>
            </div>

            <p className="text-xs text-center text-muted-foreground">
              Share your achievement with friends and family!
            </p>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShareAchievementModal;
